import React from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Flex, Text } from 'atoms';
import { EyeIconSVG } from 'utils/Svg';
import { ACL } from 'components/common';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
	width: 140px;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
`;

const ActionSection = styled(Flex)`
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
	align-self: center;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	// background: #e6f7ff;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	color: #000;
	font-size: 16px;
	transition: all 0.2s ease;
	margin-right: 15px;
	padding: 0;

	span {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px !important;
		height: 20px !important;
		margin-right: 0 !important;
	}

	svg {
		width: 20px !important;
		height: 20px !important;
		max-width: 20px;
		max-height: 20px;
	}

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	padding: 8px 18px;
	min-width: 130px;

	background: #fff;
	color: #52c41a;

	border: 1.5px solid #52c41a;
	border-radius: 6px;

	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	transition: all 0.25s ease;

	.anticon {
		font-size: 16px;
		color: #52c41a;
	}

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}

	&:active {
		background: #d9f7be;
		border-color: #52c41a;
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	min-width: 130px;

	.anticon {
		font-size: 16px;
	}

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
		transform: translateY(-1px);
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
		transform: translateY(0);
	}
`;

const formatDate = (dateString) => {
	if (!dateString) return '-';
	return moment(dateString).format('DD-MM-YYYY');
};

const formatTempControlled = (value) => {
	if (value === true || value === 'true' || value === 'Yes' || value === 'yes' || value === 1) {
		return 'Yes';
	}
	if (value === false || value === 'false' || value === 'No' || value === 'no' || value === 0) {
		return 'NO';
	}
	return value || '-';
};

export default function ClientContractCardV2({ contract, onView, onApprove, onReject, canDecide }) {
	return (
		<RequestCard>
			<CardBody>
				<InfoColumns>
					<InfoField>
						<FieldLabel>Client Name :</FieldLabel>
						<FieldValue>
							<strong>{contract.client_name || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Project Name :</FieldLabel>
						<FieldValue>
							<strong>{contract.project_name || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Billing Type :</FieldLabel>
						<FieldValue>
							<strong>{contract.billing_type || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Contract Date :</FieldLabel>
						<FieldValue>{formatDate(contract.contract_date)}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Expiry Date :</FieldLabel>
						<FieldValue>{formatDate(contract.expiry_date)}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Temp Controlled :</FieldLabel>
						<FieldValue>
							<strong>{formatTempControlled(contract.temp_controlled)}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Created by :</FieldLabel>
						<FieldValue>{contract.created_by || '-'}</FieldValue>
					</InfoField>
				</InfoColumns>
				<ActionSection>
					<ViewButton onClick={() => onView(contract.id)}>
						<EyeIconSVG />
					</ViewButton>
					{canDecide && (
						<>
							<ACL action="update" module="handle_client_contract_approval">
								<ApproveButton onClick={() => onApprove(contract.id)}>
									<CheckOutlined />
									Approve
								</ApproveButton>
							</ACL>
							<ACL action="update" module="handle_client_contract_approval">
								<RejectButton onClick={() => onReject(contract.id)}>
									<CloseOutlined />
									Reject
								</RejectButton>
							</ACL>
						</>
					)}
				</ActionSection>
			</CardBody>
		</RequestCard>
	);
}

ClientContractCardV2.propTypes = {
	contract: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		client_name: PropTypes.string,
		project_name: PropTypes.string,
		billing_type: PropTypes.string,
		contract_date: PropTypes.string,
		expiry_date: PropTypes.string,
		temp_controlled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
		created_by: PropTypes.string,
	}).isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
	canDecide: PropTypes.bool,
};

ClientContractCardV2.defaultProps = {
	canDecide: true,
};
